/**
 * 企业入驻申请（N13 / PRD §5.1.2 注册与入驻）
 *
 * 企业客户卖家开通托管资金账户的前提。流程：
 *   填写企业资料 + 上传营业执照 → 提交 → 平台审核 → 通过后自动开通
 *   「企业客户」资质、enterprise 角色与卖方托管账户。
 *
 * 使用后端真实能力：
 *   - 营业执照走 POST /upload（COS，biz=merchant_license），返回 url 后再提交表单；
 *   - 已通过（3605）/ 已有待审申请（3604）由后端拦截并给出可读文案。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CREDIT_CODE_PATTERN,
  MERCHANT_APP_STATUS,
  MERCHANT_APP_STATUS_LABEL,
  listMyMerchantApplications,
  submitMerchantApplication,
  uploadLicense,
  type MerchantApplication,
  type MerchantApplicationParams,
} from '@/api/merchant';
import { formatDateTime } from '@/utils/format';
import Empty from '@/components/common/Empty';
import PageHero from '@/components/common/PageHero';
import '@/styles/pages/wallet.css';

const STATUS_TONE: Record<number, string> = {
  [MERCHANT_APP_STATUS.PENDING]: 'warning',
  [MERCHANT_APP_STATUS.APPROVED]: 'success',
  [MERCHANT_APP_STATUS.REJECTED]: 'danger',
};

type FormState = Omit<MerchantApplicationParams, 'license_url'> & { licenseUrl: string };

const INITIAL_FORM: FormState = {
  enterprise_name: '',
  credit_code: '',
  licenseUrl: '',
  legal_person: '',
  contact_name: '',
  contact_phone: '',
  business_scope: '',
  remark: '',
};

export default function EnterpriseApply() {
  const [list, setList] = useState<MerchantApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [formMsg, setFormMsg] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setList(await listMyMerchantApplications());
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** 最新一条申请决定当前可提交状态 */
  const latest = list[0];
  const isPending = latest?.status === MERCHANT_APP_STATUS.PENDING;
  const isApproved = latest?.status === MERCHANT_APP_STATUS.APPROVED;
  const canSubmit = !isPending && !isApproved;

  const patch = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleUpload = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setFormError('营业执照需为图片文件（jpg/png 等）');
      return;
    }
    setFormError('');
    setUploading(true);
    try {
      const res = await uploadLicense(file);
      patch('licenseUrl', res.url);
      setFormMsg('营业执照已上传');
    } catch (e) {
      setFormError(e instanceof Error ? e.message : '营业执照上传失败，请重试');
    } finally {
      setUploading(false);
    }
  };

  const validate = (): string | null => {
    if (!form.enterprise_name.trim()) return '请填写企业名称';
    if (!CREDIT_CODE_PATTERN.test(form.credit_code.trim().toUpperCase())) {
      return '统一社会信用代码需为 18 位（数字 + 大写字母，不含 I/O/S/V/Z）';
    }
    if (!form.licenseUrl.trim()) return '请上传营业执照';
    if (!form.contact_name.trim()) return '请填写联系人';
    if (!/^1[3-9]\d{9}$/.test(form.contact_phone.trim())) return '请填写正确的 11 位手机号';
    return null;
  };

  const handleSubmit = async () => {
    setFormError('');
    setFormMsg('');
    const err = validate();
    if (err) {
      setFormError(err);
      return;
    }
    setSubmitting(true);
    try {
      await submitMerchantApplication({
        enterprise_name: form.enterprise_name.trim(),
        credit_code: form.credit_code.trim().toUpperCase(),
        license_url: form.licenseUrl.trim(),
        legal_person: form.legal_person?.trim() || undefined,
        contact_name: form.contact_name.trim(),
        contact_phone: form.contact_phone.trim(),
        business_scope: form.business_scope?.trim() || undefined,
        remark: form.remark?.trim() || undefined,
      });
      setFormMsg('入驻申请已提交，平台审核通过后将自动开通企业资质与资金托管账户');
      setForm(INITIAL_FORM);
      await load();
    } catch (e) {
      const ex = e as { message?: string; code?: number };
      // 3604/3605 属状态冲突，刷新列表让用户看到当前真实状态
      if (ex.code === 3604 || ex.code === 3605) {
        await load();
      }
      setFormError(ex.message ?? '提交失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="wallet-page">
      <PageHero
        eyebrow="ENTERPRISE ONBOARDING"
        title="企业入驻申请"
        desc="提交企业资质 · 平台审核 · 开通企业客户与资金托管账户"
      />
      <div className="container">
        <div className="wallet-layout">
          {/* 申请表单 */}
          <div className="wallet-panel">
            <div className="wallet-panel__head">
              填写企业资料
              <span className="wallet-hint">审核通过前不可重复提交</span>
            </div>

            {isApproved ? (
              <div className="wallet-notice">
                您已完成企业入驻，可直接使用企业客户权益与
                <Link to="/my/wallet">资金托管账户</Link>
              </div>
            ) : null}

            {isPending ? (
              <div className="wallet-notice">
                您有一条待审核的入驻申请（提交于 {formatDateTime(latest?.submitted_at)}），请耐心等待平台审核。
              </div>
            ) : null}

            <div className="wallet-form">
              <div className="form-field">
                <label className="form-label" htmlFor="mc-name">
                  <span className="req">*</span>企业名称
                </label>
                <input
                  id="mc-name"
                  className="form-control"
                  maxLength={128}
                  placeholder="与营业执照一致的全称"
                  value={form.enterprise_name}
                  onChange={(e) => patch('enterprise_name', e.target.value)}
                  disabled={!canSubmit}
                />
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor="mc-code">
                  <span className="req">*</span>统一社会信用代码
                </label>
                <input
                  id="mc-code"
                  className="form-control"
                  maxLength={18}
                  placeholder="18 位，如 91370100MA3XXXXX0A"
                  value={form.credit_code}
                  onChange={(e) => patch('credit_code', e.target.value.toUpperCase())}
                  disabled={!canSubmit}
                />
              </div>

              <div className="form-field">
                <label className="form-label">
                  <span className="req">*</span>营业执照
                </label>
                <div className="wallet-amount">
                  <input
                    className="form-control"
                    placeholder="上传后自动填充，也可直接粘贴图片地址"
                    value={form.licenseUrl}
                    onChange={(e) => patch('licenseUrl', e.target.value)}
                    disabled={!canSubmit}
                  />
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => fileRef.current?.click()}
                    disabled={!canSubmit || uploading}
                  >
                    {uploading ? '上传中…' : '上传图片'}
                  </button>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => {
                    handleUpload(e.target.files);
                    e.target.value = '';
                  }}
                />
                {form.licenseUrl && canSubmit ? (
                  <div className="wallet-hint">
                    <a href={form.licenseUrl} target="_blank" rel="noreferrer">
                      预览已上传证照
                    </a>
                  </div>
                ) : null}
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor="mc-legal">法定代表人</label>
                <input
                  id="mc-legal"
                  className="form-control"
                  maxLength={64}
                  value={form.legal_person}
                  onChange={(e) => patch('legal_person', e.target.value)}
                  disabled={!canSubmit}
                />
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor="mc-contact">
                  <span className="req">*</span>联系人
                </label>
                <input
                  id="mc-contact"
                  className="form-control"
                  maxLength={64}
                  value={form.contact_name}
                  onChange={(e) => patch('contact_name', e.target.value)}
                  disabled={!canSubmit}
                />
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor="mc-phone">
                  <span className="req">*</span>联系电话
                </label>
                <input
                  id="mc-phone"
                  className="form-control"
                  maxLength={11}
                  inputMode="numeric"
                  placeholder="11 位手机号"
                  value={form.contact_phone}
                  onChange={(e) => patch('contact_phone', e.target.value.replace(/\D/g, ''))}
                  disabled={!canSubmit}
                />
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor="mc-scope">经营范围</label>
                <input
                  id="mc-scope"
                  className="form-control"
                  maxLength={512}
                  value={form.business_scope}
                  onChange={(e) => patch('business_scope', e.target.value)}
                  disabled={!canSubmit}
                />
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor="mc-remark">备注</label>
                <input
                  id="mc-remark"
                  className="form-control"
                  maxLength={512}
                  value={form.remark}
                  onChange={(e) => patch('remark', e.target.value)}
                  disabled={!canSubmit}
                />
              </div>

              {formError ? <div className="form-error">{formError}</div> : null}
              {formMsg ? <div className="form-success">{formMsg}</div> : null}

              <button
                className="btn btn-primary btn-block"
                onClick={handleSubmit}
                disabled={submitting || !canSubmit}
              >
                {submitting ? '提交中…' : '提交入驻申请'}
              </button>

              <p className="wallet-note">
                提交后由平台审核企业资质；审核通过将自动为您开通「企业客户」身份、企业角色与卖方资金托管账户，
                可参与企业级交易与提现。
              </p>
            </div>
          </div>

          {/* 申请记录 */}
          <div className="wallet-panel">
            <div className="wallet-panel__head">申请记录</div>
            {loading ? (
              <Empty icon="⏳" title="加载中…" />
            ) : list.length === 0 ? (
              <Empty icon="🏢" title="暂无入驻申请" desc="填写左侧资料即可提交企业入驻" />
            ) : (
              <div className="wallet-records">
                {list.map((a) => (
                  <div className="wallet-record" key={a.id}>
                    <div className="wallet-record__main">
                      <div className="wallet-record__row">
                        <strong>{a.enterprise_name}</strong>
                        <span className={`wallet-tag is-${STATUS_TONE[a.status] ?? 'warning'}`}>
                          {MERCHANT_APP_STATUS_LABEL[a.status] ?? a.status}
                        </span>
                      </div>
                      <div className="wallet-record__meta">
                        <span>信用代码 {a.credit_code}</span>
                      </div>
                      <div className="wallet-record__meta">
                        <span>提交 {formatDateTime(a.submitted_at)}</span>
                        {a.reviewed_at ? (
                          <>
                            <span>·</span>
                            <span>审核 {formatDateTime(a.reviewed_at)}</span>
                          </>
                        ) : null}
                      </div>
                      {a.status === MERCHANT_APP_STATUS.REJECTED && a.reject_reason ? (
                        <div className="wallet-record__reject">驳回原因：{a.reject_reason}</div>
                      ) : null}
                    </div>
                    <div className="wallet-record__side">
                      <span>申请编号</span>
                      <b>#{a.id}</b>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
